import { type ReactNode, type Ref, useState } from "react";
import {
  type NativeSyntheticEvent,
  Platform,
  Pressable,
  type PressableProps,
  StyleSheet,
  type StyleProp,
  type View,
  type ViewStyle,
} from "react-native";

import { useTheme } from "@/theme/useTheme";

import { Text } from "./Text";

export type PressableTargetProps = Omit<
  PressableProps,
  "children" | "style" | "accessibilityRole"
> & {
  accessibilityRole?: "button" | "link" | "tab" | "radio" | "menuitem";
  /** Required when the target has no text child (icon-only control, FR-044). */
  accessibilityLabel?: string;
  children: ReactNode;
  style?: StyleProp<ViewStyle>;
  /** Web only: merged over `style` while the pointer is over the target. */
  hoveredStyle?: StyleProp<ViewStyle>;
  /** React 19 passes `ref` as a prop; it reaches the Pressable (focus management, 002 menus). */
  ref?: Ref<View>;
};

/**
 * On the web a click or tap also focuses the target; only keyboard focus (`:focus-visible`) shows
 * the ring, so it never lingers on a button the user just pressed.
 */
function showsFocusRing(e: NativeSyntheticEvent<unknown>): boolean {
  if (Platform.OS !== "web") return true;
  const target = (e.nativeEvent as { target?: Element } | undefined)?.target;
  try {
    return target?.matches?.(":focus-visible") ?? true;
  } catch {
    return true;
  }
}

/**
 * The one pressable primitive: at least 44 x 44 (FR-044), a real accessible role, a label for
 * icon-only use, and the `focusRing` token outline when keyboard-focused on the web (FR-045).
 */
export function PressableTarget({
  accessibilityRole = "button",
  accessibilityLabel,
  children,
  style,
  hoveredStyle,
  onFocus,
  onBlur,
  ...rest
}: PressableTargetProps) {
  const theme = useTheme();
  const [focused, setFocused] = useState(false);
  const isText = typeof children === "string" || typeof children === "number";

  if (!isText && !accessibilityLabel) {
    throw new Error(
      "PressableTarget needs an accessibilityLabel when it has no text child (FR-044)",
    );
  }

  return (
    <Pressable
      {...rest}
      accessibilityRole={accessibilityRole}
      accessibilityLabel={
        accessibilityLabel ?? (isText ? String(children) : undefined)
      }
      onFocus={(e) => {
        setFocused(showsFocusRing(e));
        onFocus?.(e);
      }}
      onBlur={(e) => {
        setFocused(false);
        onBlur?.(e);
      }}
      // Press feedback: a ripple on Android, a dimmed target on the web.
      android_ripple={rest.android_ripple ?? { color: theme.color.border }}
      style={(state) =>
        StyleSheet.flatten([
          {
            minWidth: theme.size.touchTarget,
            minHeight: theme.size.touchTarget,
            alignItems: "center",
            justifyContent: "center",
          },
          focused && {
            outlineColor: theme.color.focusRing,
            outlineWidth: theme.borderWidth.focusRing,
            outlineStyle: "solid",
            outlineOffset: theme.borderWidth.focusRing,
            borderRadius: theme.radius.button,
          },
          state.pressed && Platform.OS === "web" && { opacity: 0.72 },
          style,
          // React Native Web adds `hovered` to the state; native never sets it.
          (state as { hovered?: boolean }).hovered && hoveredStyle,
        ])
      }
    >
      {isText ? <Text weight="semiBold">{children}</Text> : children}
    </Pressable>
  );
}
